import { round, floor, ceil, isNil } from 'lodash-es';
import Big from 'big.js';
import {
  isApiCountUnit,
  convertApiDataUnitToBinaryByte,
  convertTimeApiUnit,
  isApiTimeUnit,
  isApiDataUnit,
  isDataTransmissionUnit,
  isApiCoreUnit,
  ApiDataUnit,
  DataTransmissionUnit,
} from '../adaptors/convertUnits';
import { BinaryBytesUnit, CoreUnit, CountUnit, TimeUnit } from './convertUnits.types';

export type UnitValueInfo<Unit = string> = {
  value: number;
  unit: Unit;
};

type ConvertedUnitValueInfo<Unit = string> = UnitValueInfo<Unit>;

const convertValueWithUnitList = <Unit extends string>(
  value: number,
  unit: Unit,
  unitRange: number,
  units: Unit[],
): ConvertedUnitValueInfo<Unit> => {
  const sign = value < 0 ? -1 : +1;
  let unitInd = units.findIndex((unitItem) => unitItem === unit);
  let copiedValue = value / sign;

  if (unitInd === -1) {
    return {
      value,
      unit,
    };
  }

  while (copiedValue >= unitRange && unitInd < units.length - 1) {
    copiedValue /= unitRange;
    unitInd++;
  }
  return {
    value: copiedValue * sign,
    unit: units[unitInd],
  };
};

export const isCountUnit = (unit: any): unit is CountUnit => {
  return CountUnit.includes(unit);
};

export const convertCountWithUnit = (
  count: number,
  toUnit?: CountUnit,
): ConvertedUnitValueInfo<CountUnit> => {
  if (toUnit != null) {
    return {
      value: count / 1000 ** CountUnit.indexOf(toUnit),
      unit: toUnit,
    };
  }
  return convertValueWithUnitList(count, '', 1000, [...CountUnit]);
};

const convertBinaryBytesWithUnit = (
  byte: number,
  unit: BinaryBytesUnit,
  toUnit?: BinaryBytesUnit,
): ConvertedUnitValueInfo<BinaryBytesUnit> => {
  if (toUnit) {
    const fromUnitIndex = BinaryBytesUnit.indexOf(unit);
    const toUnitIndex = BinaryBytesUnit.indexOf(toUnit);

    if (fromUnitIndex === toUnitIndex) {
      return { value: byte, unit: toUnit };
    }
    return {
      value:
        fromUnitIndex > toUnitIndex
          ? byte * 1024 ** (fromUnitIndex - toUnitIndex)
          : byte / 1024 ** (toUnitIndex - fromUnitIndex),
      unit: toUnit,
    };
  }
  return convertValueWithUnitList(byte, unit, 1024, [...BinaryBytesUnit]);
};

const isTimeUnit = (unit: any): unit is TimeUnit => {
  return TimeUnit.includes(unit);
};

const convertValueUnitCsToMs = (value: number): ConvertedUnitValueInfo<'ms'> => {
  // 1cs === 10ms
  return { value: value * 10, unit: 'ms' };
};

/**
 ** ms, cs 단위는 모두 s로 변경하고, 그외의 단위는 그대로 보여준다.
 */
const convertTimeWithUnit = (
  time: number,
  unit: TimeUnit | 'cs',
  toUnit?: TimeUnit,
): ConvertedUnitValueInfo<TimeUnit> => {
  let timeUnit = unit;
  let timeValue = time;
  if (timeUnit === 'cs') {
    const { value: msValue, unit: convertedMsUnit } = convertValueUnitCsToMs(time);
    timeValue = msValue;
    timeUnit = convertedMsUnit;
  }

  let timeUnitIndex = TimeUnit.findIndex((timeUnitItem) => timeUnitItem === timeUnit);

  if (timeUnitIndex === -1) {
    return { value: time, unit: timeUnit };
  }

  const MsPerSecond = 1000;
  const SecondsPerMinute = 60;
  const secondIndex = TimeUnit.findIndex((timeUnitItem) => timeUnitItem === 's');
  if (secondIndex === -1) {
    throw new Error('s unit이 TimeUnit에서 제외되었습니다.');
  }

  const toTimeUnit = toUnit || (timeUnit === 'ms' ? 's' : timeUnit);

  if (toTimeUnit) {
    const toTimeUnitIndex = TimeUnit.findIndex((timeUnitItem) => timeUnitItem === toTimeUnit);
    if (timeUnitIndex > toTimeUnitIndex) {
      while (TimeUnit[timeUnitIndex] !== toTimeUnit) {
        timeValue *= timeUnitIndex <= secondIndex ? MsPerSecond : SecondsPerMinute;
        --timeUnitIndex;
      }
    } else {
      while (TimeUnit[timeUnitIndex] !== toTimeUnit) {
        timeValue /= timeUnitIndex < secondIndex ? MsPerSecond : SecondsPerMinute;
        ++timeUnitIndex;
      }
    }
    return {
      value: timeValue,
      unit: TimeUnit[timeUnitIndex],
    };
  }

  if (timeUnitIndex < 2) {
    while (TimeUnit[timeUnitIndex] !== 's') {
      timeValue /= MsPerSecond;
      ++timeUnitIndex;
    }
    return {
      value: timeValue,
      unit: TimeUnit[timeUnitIndex],
    };
  }
  return { value: time, unit: timeUnit };
};

const getDataConvertedUnit = (originUnit: string, convertedUnit: BinaryBytesUnit) => {
  if (originUnit.includes('/sec')) {
    return `${convertedUnit}/sec`;
  }
  return convertedUnit;
};

/**
 * 전송률(bps 계열) 단위 변환 — 네트워크 관례(SI 십진, 1 kbps = 1000 bps)를 따른다.
 * byte 계열의 1024(binary) 변환과 구분된다 (RFC 2863 ifHighSpeed 등 rate 지표는 십진 기반).
 */
const convertTransmissionWithUnit = (
  value: number,
  unit: DataTransmissionUnit,
  toUnit?: DataTransmissionUnit,
): ConvertedUnitValueInfo<DataTransmissionUnit> => {
  if (toUnit != null) {
    const diff = DataTransmissionUnit.indexOf(toUnit) - DataTransmissionUnit.indexOf(unit);
    return {
      value: value / 1000 ** diff,
      unit: toUnit,
    };
  }
  return convertValueWithUnitList<DataTransmissionUnit>(value, unit, 1000, [
    ...DataTransmissionUnit,
  ]);
};

const isCoreUnit = (unit: any): unit is CoreUnit => {
  return CoreUnit.includes(unit);
};

const convertCoreWithUnit = (
  value: number,
  unit: CoreUnit,
  toUnit?: CoreUnit,
): ConvertedUnitValueInfo<CoreUnit> => {
  if (toUnit != null) {
    const diff = CoreUnit.indexOf(toUnit) - CoreUnit.indexOf(unit);
    return {
      value: value / 1000 ** diff,
      unit: toUnit,
    };
  }
  return convertValueWithUnitList<CoreUnit>(value, unit, 1000, [...CoreUnit]);
};

export type ConvertValueToUnits = ApiDataUnit | CountUnit | CoreUnit | TimeUnit | '%';
export const convertValueWithUnit = (
  { value, unit }: UnitValueInfo,
  options?: {
    decimal?: number;
    toUnit?: ConvertValueToUnits;
    originUnit?: boolean;
    space?: boolean;
    roundingMode?: 'round' | 'floor' | 'ceil' | 'none';
  },
): ConvertedUnitValueInfo => {
  const {
    decimal = undefined,
    toUnit,
    originUnit = false,
    space = true,
    roundingMode = 'round',
  } = options ?? {};

  const mathFunction = {
    floor,
    ceil,
    round,
    none: (v: number, _decimal: number | undefined) => v,
  } as const;
  const roundingFn = mathFunction[roundingMode];

  const getDecimal = (v: number) => {
    if (isNil(decimal)) {
      return v >= 1 ? 3 : 20;
    }

    return decimal;
  };

  const lowerUnit = unit?.toLowerCase() ?? unit;
  if (isApiCountUnit(unit)) {
    const { value: countValue, unit: convertedCountUnit } = convertCountWithUnit(
      value,
      isCountUnit(toUnit) ? toUnit : undefined,
    );

    return {
      value: roundingFn(countValue, getDecimal(countValue)),
      unit: convertedCountUnit
        ? `${convertedCountUnit}${originUnit ? '' : `${space ? ' ' : ''}${unit}`}`
        : unit,
    };
  }
  if (isDataTransmissionUnit(unit)) {
    const { value: transmissionValue, unit: convertedTransmissionUnit } =
      convertTransmissionWithUnit(value, unit, isDataTransmissionUnit(toUnit) ? toUnit : undefined);

    return {
      value: roundingFn(transmissionValue, getDecimal(transmissionValue)),
      unit: convertedTransmissionUnit,
    };
  }
  if (isApiDataUnit(unit)) {
    const { value: countValue, unit: convertedDataUnit } = convertBinaryBytesWithUnit(
      value,
      convertApiDataUnitToBinaryByte(unit),
      isApiDataUnit(toUnit) ? convertApiDataUnitToBinaryByte(toUnit) : undefined,
    );

    return {
      value: roundingFn(countValue, getDecimal(countValue)),
      unit: getDataConvertedUnit(unit, convertedDataUnit),
    };
  }
  if (isApiTimeUnit(unit)) {
    const { value: convertedValue, unit: convertedUnit } = convertTimeWithUnit(
      value,
      convertTimeApiUnit(unit),
      isTimeUnit(toUnit) ? toUnit : undefined,
    );
    return {
      value: roundingFn(convertedValue, getDecimal(convertedValue)),
      unit: convertedUnit,
    };
  }

  if (isApiCoreUnit(lowerUnit)) {
    const { value: convertedValue, unit: convertedUnit } = convertCoreWithUnit(
      value,
      lowerUnit,
      isCoreUnit(toUnit) ? toUnit : undefined,
    );
    return {
      value: roundingFn(convertedValue, getDecimal(convertedValue)),
      unit: convertedUnit,
    };
  }
  return { value: roundingFn(value, getDecimal(value)), unit };
};

export const formatValueWithUnit = ({
  value,
  unit,
  originValue,
}: ConvertedUnitValueInfo & { originValue: number }): string => {
  const computedUnitStr = unit ? ` ${unit}` : '';
  if (originValue === 0) return `0${computedUnitStr}`;
  if (value === 0) return `0.000${computedUnitStr}`;
  return `${value}${computedUnitStr}`;
};

/**
 * byte 값을 자동 단위 변환하여 포맷된 문자열로 반환
 * @param value - 변환할 byte 값
 * @param options - 변환 옵션
 * @param options.unit - 입력 단위 (default: 'byte')
 * @param options.toUnit - 출력 단위 (default: 'auto')
 * @param options.decimal - 소수점 자리수 (default: 2)
 * @returns 포맷된 문자열 (예: "1.5 GB")
 */
export const getFormattedByteValue = (
  value: number | null | undefined,
  options?: {
    unit?: string;
    toUnit?: ConvertValueToUnits;
    decimal?: number;
  },
): string | null => {
  if (isNil(value)) return null;

  const { unit = 'byte', toUnit, decimal = 3 } = options ?? {};
  const { value: convertedValue, unit: convertedUnit } = convertValueWithUnit(
    { value, unit },
    { toUnit, decimal },
  );

  const formattedValue =
    typeof convertedValue === 'number'
      ? convertedValue.toLocaleString('en-US', { maximumFractionDigits: decimal })
      : convertedValue;

  return toUnit ? `${formattedValue}` : `${formattedValue} ${convertedUnit}`;
};

/**
 * 숫자 문자열을 세 번째 유효숫자 기준으로 반올림 후 지수 표기로 변환한다.
 * 첫 번째~두 번째 유효숫자를 표시하며, trailing zero는 제거한다.
 *
 * ex) "0.0004108" → 유효숫자 {4,1,0,8}, 세번째(0) 기준 반올림 → "4.1e-4"
 * ex) "0.000039"  → 유효숫자 {3,9}, 세번째 없음 → "3.9e-5"
 * ex) "0.00003945678" → 유효숫자 {3,9,4,...}, 세번째(4) 기준 반올림 → "3.9e-5"
 * ex) "0.00003951" → 유효숫자 {3,9,5,...}, 세번째(5) 기준 반올림 → 4.0e-5 → trim → "4e-5"
 *
 * @param numStr - 숫자 문자열 (지수표기가 아닌 값)
 * @returns string - 세 번째 유효숫자 기준으로 축약된 지수 표기 문자열
 */
export const toExponentialTrimmed = (numStr: string): string => {
  const big = new Big(numStr.replace(/,/g, ''));

  // toExponential(1) = 첫번째~두번째 유효숫자를 표시하고 세번째 유효숫자로 반올림 판단
  // ex) 0.0004108 → "4.1e-4", 0.000039 → "3.9e-5"
  const exp = big.toExponential(1);

  const [base, exponent] = exp.split('e');

  // trailing zero 제거: "4.0" → "4", "3.9" → "3.9"
  const trimmedBase = base.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

  return `${trimmedBase}e${exponent}`;
};

/**
 * 'count' 단위는 제거하고, 나머지 단위는 그대로 반환한다.
 *
 * @param unit - 단위 문자열
 * @returns string - 'count' 단위는 제거하고, 나머지 단위는 그대로 반환한다.
 */
export const normalizeUnit = (unit?: string): string => {
  return unit?.toLowerCase() === 'count' ? '' : unit || '';
};
